import { useCallback, useEffect, useMemo, useRef } from "react"
import { useStores } from "../../../models/helpers/useStores"
import styles from "./styles.module.scss"
import { TripNavigatorState, TypingUser } from "./NavigatorStore"
import { MessageRenderer } from "../../navigator/MessageRenderer"
import {
  NavigatorSystemMessageType,
  NavigatorMessage,
  NavigatorUserMessageType,
} from "@cruisen/contract"
import { observer } from "mobx-react-lite"
import ArrowDownwardOutline from "~icons/eva/arrow-downward-outline"
import AlertCircleOutline from "~icons/eva/alert-circle-outline"
import { DefaultSpinner } from "../../loaders/DefaultSpinner/index.tsx"
import { TypingIndicator } from "../../atoms/TypingIndicator"
import { NavigatorFile, NavigatorInput } from "./components/NavigatorInput"
import { ImagePreviewModal } from "../../modals/ImagePreviewModal"
import { DocumentPreviewModal } from "../../modals/DocumentPreviewModal"
import { mergeNavigatorMessages } from "./utils/mergeNavigatorMessages.ts"
import { callFirebaseFunction } from "../../../services/callFirebaseFunction.ts"
import { NavigatorFilesButton } from "./components/NavigatorFilesButton"
import HelmIcon from "../../assets/icons/HelmIcon.tsx"
import { colors } from "@theme/colors"
import { Popover } from "../../atoms/Popover/index.tsx"
import SlidersIcon from "../../assets/icons/SlidersIcon.tsx"
import classNames from "classnames"
import { NavigatorFiltersModal } from "./components/NavigatorFiltersModal/index.tsx"

const Footer = (props: {
  context: { isNavigatorLoading: boolean; typingUserList: TypingUser[] }
}) => {
  return (
    <div className={styles.navigatorFooter}>
      {props.context.isNavigatorLoading || props.context.typingUserList.length > 0 ? (
        <TypingIndicator
          isNavigatorLoading={props.context.isNavigatorLoading}
          typingUserList={props.context.typingUserList}
        />
      ) : null}
    </div>
  )
}

const Header = () => (
  <div className={styles.navigatorHeader}>
    <DefaultSpinner />
  </div>
)

export const Navigator = observer(function Navigator() {
  const rootStore = useStores()
  const state = useMemo(() => new TripNavigatorState(), [])
  // Messages data is reversed to show the latest messages at the bottom
  const selectedMessageTypes = state.navigatorMessageTypes.join(",")
  const filteredMessages = useMemo(() => {
    if (state.navigatorMessageTypes.length === 0) return state.messages

    return state.messages.filter(
      (
        message: NavigatorMessage & {
          type: NavigatorSystemMessageType | NavigatorUserMessageType
        },
      ) => {
        if (message?.type === "paymentRequest") return state.navigatorMessageTypes.includes("quote")
        if (
          message?.type === "userSetPreferredCruise" ||
          message?.type === "navigatorCruiseRecommendation" ||
          message?.type === "userLikedTripSearchResult"
        )
          return state.navigatorMessageTypes.includes("cruise")
        return false
      },
    )
  }, [state.messages.length, state.currentTripId, selectedMessageTypes])

  const messagesData = useMemo(
    () => mergeNavigatorMessages(filteredMessages, rootStore.loggedInUser.id),
    [filteredMessages.length],
  )

  const messageListRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const messageObserverCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const lastEntry = entries[entries.length - 1]
      if (!lastEntry?.isIntersecting) return

      const messageId = lastEntry.target.getAttribute("data-message-id")
      if (!messageId) return
      const messageIndex = Number(messageId)

      const scrollContainer = messageListRef.current
      if (!scrollContainer) return

      if (messageIndex >= messagesData.length - 2 && state.hasMore) {
        state.loadMoreMessages(() => null)
      }

      const shouldShowScrollButton = messageIndex > 25
      state.setScrollButtonVisible(shouldShowScrollButton)

      const atBottom = messageIndex < 10
      if (atBottom) {
        state.trackLastVisibleMessage(messagesData[0])
      }

      if (atBottom !== state.scrolledToBottom) {
        state.setScrolledToBottom(atBottom)
      }
    },
    [messagesData.length],
  )

  useEffect(() => {
    if (!messageListRef.current) return

    const observer = new IntersectionObserver(messageObserverCallback, {
      root: messageListRef.current,
      threshold: 0.5,
    })

    observerRef.current = observer

    const messageElements = messageListRef.current.querySelectorAll("[data-message-id]")
    messageElements?.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [messageObserverCallback])

  const scrollToBottom = () => {
    setTimeout(() => {
      messageListRef?.current?.firstElementChild?.scrollIntoView({
        behavior: "instant",
      })
      state.setScrollButtonVisible(false)
      state.setScrolledToBottom(true)
    }, 0)
  }

  useEffect(() => {
    if (!rootStore.activeTrip?.twilioConversationSid) return

    const savedMessages = rootStore.messages.toJSON()
    state.clearStore(savedMessages[rootStore.activeTrip.id], rootStore.activeTrip.id)
    state.initializeClient(
      rootStore.activeTrip.twilioConversationSid as string,
      rootStore.activeTrip.id,
      () => scrollToBottom(),
    )
    scrollToBottom()

    return () => {
      state.clearStore()
    }
  }, [rootStore.activeTrip?.twilioConversationSid])

  useEffect(() => {
    if (state.isPendingLoadMore) {
      state.loadMoreMessages()
    }
  }, [state.isPendingLoadMore, state.messagesPaginator])

  const noMessagesFound = filteredMessages.length === 0 && !state.hasMore
  useEffect(() => {
    if (filteredMessages.length === 0 && state.hasMore) {
      state.loadMoreMessages()
    }
  }, [state.messages.length, filteredMessages.length])

  useEffect(() => {
    state.setNavigatorMessageTypes([])
  }, [rootStore.activeTrip?.id])

  const submitForm = async (message: string, fileData: NavigatorFile | null) => {
    if (!rootStore.activeTrip || !rootStore.loggedInUser || !state.conversation) return

    if (
      rootStore.loggedInUser.isSuperAdmin &&
      !rootStore.activeTrip.collaboratorIds?.includes(rootStore.loggedInUser.id)
    ) {
      try {
        let participantSid: string | null = null
        try {
          const participant = await state.conversation.getParticipantByIdentity(
            rootStore.loggedInUser.email,
          )
          participantSid = participant.sid
        } catch (error) {
          if (!error.message.includes("not found")) {
            throw error
          }
        }

        if (!participantSid) {
          await callFirebaseFunction({
            name: "joinTrip",
            payload: {
              tripId: rootStore.activeTrip.id,
              onlyJoinConversation: true,
            },
          })
        }
      } catch (error) {
        alert("Trip join failure!")
        console.log("Trip join failure", error)
      }
    }

    await state.sendMessage({
      tripId: rootStore.activeTrip.id,
      loggedInUserId: rootStore.loggedInUser.id,
      firstName: rootStore.loggedInUser.firstName,
      lastName: rootStore.loggedInUser.lastName,
      avatar: rootStore.loggedInUser.avatar || "",
      message: message,
      inputType: state.inputType,
      fileData,
      isSuperAdmin: rootStore.loggedInUser.isSuperAdmin,
    })
  }

  if (!rootStore.activeTrip || !rootStore.loggedInUser) return null

  const currentUserId = rootStore.loggedInUser?.id
  const currentUserRoom = rootStore.activeTrip.rooms?.find((room) =>
    room.guests?.find((guest) => guest.userId === currentUserId),
  )
  const tripOwnerId = rootStore.activeTrip.ownerId

  return (
    <div className={styles.container} data-test-id="navigator-container">
      <div className={styles.chatContainer}>
        <div className={styles.chatHeading}>
          <div className={styles.titleContainer}>
            <HelmIcon color={colors.iconPrimary} height={18} width={18} />
            <h1 className={styles.title}>Navigator</h1>
            {state.isLoading && messagesData.length > 0 ? <DefaultSpinner size="sm" /> : null}
          </div>
          <div className={styles.navigatorActionsRow}>
            <NavigatorFilesButton
              activeTripId={rootStore.activeTrip.id}
              setSelectedItem={(item) => state.setSelectedItem(item)}
              isFilesModalVisible={state.activeModal === "files"}
              toggleIsFilesModalVisible={() => state.toggleActiveModal("files")}
            />
            <Popover
              isOpen={state.activeModal === "filters"}
              wrapperClassName={styles.filteringPopover}
              onClickOutside={() => {
                state.setActiveModal(null)
              }}
              align="end"
              title="Filters"
              testId="filtering-popover"
              content={
                <NavigatorFiltersModal
                  selectedValues={state.navigatorMessageTypes}
                  onSubmit={(value) => {
                    state.setActiveModal(null)
                    state.setNavigatorMessageTypes(value)
                  }}
                />
              }
            >
              <button
                className={classNames(
                  styles.navigatorActionButton,
                  state.navigatorMessageTypes.length > 0 && styles.navigatorActionButtonSelected,
                )}
                onClick={() => state.toggleActiveModal("filters")}
              >
                <SlidersIcon />
              </button>
            </Popover>
          </div>
        </div>
        <>
          {state.navigatorError ? (
            <div className={styles.errorWrapper}>
              <div className={styles.errorContainer}>
                <AlertCircleOutline className={styles.errorIcon} />
                <p className={styles.errorTitle}>Oops! Something went wrong.</p>
                <p className={styles.errorDescription}>
                  We're unable to connect to the Navigator service. Please check any firewall
                  settings, or contact support.
                </p>
              </div>
            </div>
          ) : (
            <>
              {messagesData && messagesData.length > 0 && !noMessagesFound ? (
                <div className={styles.messageListContainer}>
                  <div
                    className={styles.messageList}
                    ref={messageListRef}
                    data-testid="navigator-message-list"
                  >
                    <Footer
                      context={{
                        isNavigatorLoading: rootStore?.activeTrip?.isNavigatorLoading,
                        typingUserList: state.typingUsersList,
                      }}
                    />
                    {messagesData.map((message, index) => (
                      <div key={message.id} data-message-id={index}>
                        <MessageRenderer
                          message={message}
                          showAvatarAndName={message.showAvatarAndName}
                          showMessageDate={message.showMessageDate}
                          currentUserRoomId={currentUserRoom?.id}
                          currentUserRoomName={currentUserRoom?.name}
                          currentUserId={currentUserId}
                          tripOwnerId={tripOwnerId}
                        />
                      </div>
                    ))}
                    {state.loadingMore ? <Header /> : null}
                  </div>
                  {state.scrollButtonVisible ? (
                    <button className={styles.scrollButton} onClick={() => scrollToBottom()}>
                      <ArrowDownwardOutline />
                    </button>
                  ) : null}
                </div>
              ) : (
                <>
                  {noMessagesFound && !state.isLoading ? (
                    <div className={styles.noMessagesFoundContainer}>
                      <p className={styles.noMessagesFoundTitle}>No messages found</p>
                      <p className={styles.noMessagesFoundDescription}>
                        Please clear navigator filters
                      </p>
                    </div>
                  ) : (
                    <div className={styles.loadingContainer}>
                      <DefaultSpinner />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
        {!state.navigatorError ? (
          <NavigatorInput
            submitForm={submitForm}
            onTyping={() => state.onChangeText()}
            toggleInputType={() =>
              state.setInputType(state.inputType === "message" ? "navigatorAI" : "message")
            }
            inputType={state.inputType}
          />
        ) : null}
      </div>
      {state.selectedItem ? (
        <>
          {state.selectedItem.isImage ? (
            <ImagePreviewModal
              files={[
                {
                  fileName: state.selectedItem.name,
                  fileUrl: state.selectedItem.url,
                },
              ]}
              onClose={() => state.setSelectedItem(null)}
            />
          ) : (
            <DocumentPreviewModal
              fileUrl={state.selectedItem.url}
              onClose={() => state.setSelectedItem(null)}
            />
          )}
        </>
      ) : null}
    </div>
  )
})
